import {
  Typography,
  Tooltip,
  useTheme,
  useMediaQuery,
  Box,
  Divider,
} from "@mui/material";
import Stack from "@mui/material/Stack";

import React, { useState } from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import { ReactComponent as CollectionsLogo } from "assets/icons/bookmark-blue-icon.svg";
import { ReactComponent as CollectionsLogoHover } from "assets/icons/bookmark-hover-icon.svg";

import { useDispatch } from "react-redux";
import {
  setCollectionId,
  setCollectionSourceId,
  setEntityName,
  setIsCitationAvailable,
  setIsSidebarOpen,
  setIsSourceSidebarOpen,
  setIsSummaryAvailable,
  setLevel,
  setReadingLevelAvailable,
  setSelectedBookmarkOption,
  setSourceUrl,
  setSummaryInfoData,
} from "context/features/dataSlice";
import { VscLock } from "react-icons/vsc";
import { logEvent } from "services/amplitude.service.js";
import { getRegularDate } from "utils/time.util";
import { useIsTab } from "hooks/is-tab.hook";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import OpenAccessChip from "../open-access-chip";
import SmileyIcon from "features/News/components/smiley-icon";
import AccessibleIconButton from "components/base/AccessibleIconButton";

export const AcademiaCard = ({
  data,
  cardRef,
  category,
  query,
  academiaSearch,
  academiaLabel,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTab = useIsTab();
  const dispatch = useDispatch();

  const openSourceSidebar = () => {
    logEvent("click_card_header", {
      host: data.hostName,
      entity: data.entityName,
    });
    dispatch(setIsSourceSidebarOpen(true));
    dispatch(setSourceUrl(data.hostName));
    dispatch(setEntityName(data.entityName));
  };

  const CardHeader = () => {
    return (
      <Stack
        mb={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={1}
        sx={{
          cursor: "pointer",
          "&:hover": { backgroundColor: "secondary.main" },
          py: 0.4,
          px: 1,
          borderRadius: 1,
        }}
        onClick={openSourceSidebar}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: "bold",
            width: { xs: "20rem", lg: "40rem" },
            cursor: "pointer",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "rgb(126,127,126)",
          }}
        >
          {data.entityName}
        </Typography>
        <Stack alignItems="center" direction="row" gap={2}>
          {data.hasOpenAccess && <OpenAccessChip />}
        </Stack>
      </Stack>
    );
  };

  const openSidebar = () => {
    dispatch(setLevel(""));
    logEvent("open_bookmark_popup", {
      url: data.url,
      query: query,
      entity: data.entityName,
      category: category,
      type: "academia",
      mediabiasrating: data.mediaBiasRating ? data.mediaBiasRating : "NA",
    });
    dispatch(setSelectedBookmarkOption("notes"));

    dispatch(
      setSummaryInfoData({
        publisher: data.entityName,
        title: data.title,
        published_date: data.datePublished,
        url: data.url,
        utc_date_published: data.datePublished,
      })
    );

    dispatch(setCollectionSourceId(data.collectionSourceId));
    dispatch(setCollectionId(data.collectionId));
    dispatch(setIsCitationAvailable(data.isCitationAvailable));
    dispatch(setIsSummaryAvailable(data.isSummaryAvailable));
    dispatch(setReadingLevelAvailable(data.isReadingLevelAvailable));
    dispatch(setIsSidebarOpen(true));
  };

  const BookmarkOptions = () => {
    return (
      <Stack
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          cursor: "pointer",
          mt: "auto",
          borderRadius: 1,
          "&:hover": {
            backgroundColor: "secondary.main",
          },
          pl: 1,
          pr: 1,
        }}
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
        mt="auto"
      >
        {data.collectionSourceId ? (
          <CollectionsLogo
            onClick={openSidebar}
            alt="Collections"
            style={{
              width: 18,
              height: 18,
              fill: "blue",
              color: "blue",
            }}
          />
        ) : (
          <Box
            sx={{
              width: 18,
              height: 18,
              cursor: "pointer",
            }}
          >
            {isHovered && (
              <CollectionsLogoHover
                onClick={openSidebar}
                alt="Collections"
                style={{
                  fill: "blue",
                  color: "blue",
                }}
              />
            )}
          </Box>
        )}
        <AccessibleIconButton
          sx={{
            p: 0,
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
          title="Open Bookmark"
          onClick={openSidebar}
        >
          <MoreHorizIcon />
        </AccessibleIconButton>
      </Stack>
    );
  };

  const NotesSection = () => {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          mt: 1,
          gap: 2,
          px: 1,
          justifyContent: "space-between",
        }}
      >
        <Stack alignContent="end">
          <Typography
            variant={isMobile ? "body1" : "body2"}
            sx={{
              color: "grey.600",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: { xs: 3 },
              WebkitBoxOrient: "vertical",
              maxHeight: { xs: "6vh" },
              lineHeight: { xs: "2vh" },
              width: "98%",
            }}
          >
            {data.datePublished && `${getRegularDate(data.datePublished)} - `}
            {data.description}
          </Typography>
          <Typography
            variant={isMobile ? "body1" : "body2"}
            sx={{
              color: "grey.600",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: { xs: 1 },
              WebkitBoxOrient: "vertical",
              maxHeight: { xs: "2vh" },
              lineHeight: { xs: "2vh" },
              width: "98%",
            }}
          >{`Author - ${data.authors}`}</Typography>
        </Stack>
        <BookmarkOptions />
      </Box>
    );
  };

  const handleTitleClick = () => {
    logEvent("click_resultset_title", {
      url: data.url,
      query: query,
      entity: academiaSearch,
      category: category,
      type: "academia",
      mediabiasrating: data.mediaBiasRating ? data.mediaBiasRating : "NA",
    });
    window.open(data.url, "_blank");
  };

  return (
    <Box
      ref={cardRef}
      sx={{
        width: "100%",
        boxShadow: "none",
        padding: "0 1rem",
        boxShadow: "none",
      }}
    >
      <Stack direction="row" alignItems="center">
        <Box sx={{ flexGrow: 1, flexBasis: "100%", px: 1 }}>
          <Stack direction="column" gap={1} justifyContent="space-between">
            <Box>
              <CardHeader />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                  px: 1,
                }}
              >
                <SearchResultLink
                  url={data.url}
                  trackingCat="click_resultset_title"
                  trackingOptions={{
                    url: data.url,
                    query: query,
                    entity: data.entityName,
                    title: data.title,
                    category: category,
                    mediabiasrating: "NA",
                    score: "NA",
                    type: "academia",
                    date_published: data.datePublished,
                  }}
                  variant="body4"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    maxHeight: "5em",
                    lineHeight: "1.5em",
                    maxWidth: "100%",
                    flexShrink: 0,
                    cursor: "pointer",
                    "&:hover": {
                      color: theme.palette.primary.light,
                    },
                  }}
                >
                  {data.title}
                </SearchResultLink>
                {data.hasPayWall && data.isPartner ? (
                  <SmileyIcon />
                ) : (
                  data.hasPayWall && (
                    <Tooltip
                      placement="bottom-start"
                      title="This source may limit your access without a subscription"
                    >
                      <Box sx={{ cursor: "pointer" }}>
                        <VscLock />
                      </Box>
                    </Tooltip>
                  )
                )}
              </Box>
            </Box>
          </Stack>
          <NotesSection />
          <Divider sx={{ mt: "1vh" }} />
        </Box>
      </Stack>
    </Box>
  );
};
